import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Info, Loader2, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MemberLayout from "../components/MemberLayout";
import { api, formatApiError } from "../lib/api";
import { getAvatarSrc } from "../lib/avatar";
import { formatDate } from "../lib/dateFormat";
import { PROFESSION_OPTIONS } from "../data/professionOptions";
import {
  COUNTRY_PHONE_OPTIONS,
  INDIA_COUNTRY_OPTION,
} from "../data/phoneCountries";

const PROFILE_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const BUSINESS_DESCRIPTION_MAX_LENGTH = 500;

const splitBusinessDetails = (value = "") => {
  const parts = value.split("|").map((part) => part.trim());
  return {
    business_name: parts[0] || "",
    role: parts[1] || "",
    experience: parts[2] || "",
    business_description: parts.slice(3).join(" | "),
  };
};

const composeBusinessDetails = (source) =>
  [
    source.business_name,
    source.role,
    source.experience,
    source.business_description,
  ]
    .map((value) => (value || "").trim())
    .filter(Boolean)
    .join(" | ");

const statusLabel = (value = "") =>
  value
    ? value
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "—";

export default function MemberProfile() {
  const { member, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [form, setForm] = useState({
    business_name: "",
    role: "",
    experience: "",
    business_description: "",
    city: "",
    phone: "",
  });
  const [profession, setProfession] = useState("");
  const [customProfession, setCustomProfession] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(INDIA_COUNTRY_OPTION);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const fileRef = useRef(null);

  // =========================
  // LOAD DATA
  // =========================
  useEffect(() => {
    if (member) {
      const memberProfession = member.profession || "";
      const parsedWork = splitBusinessDetails(member.business_details || "");
      const knownProfession = PROFESSION_OPTIONS.some(
        (option) => option.value === memberProfession,
      );
      const memberCountry =
        COUNTRY_PHONE_OPTIONS.find(
          (option) =>
            option.iso === member.country_code ||
            option.dial === member.phone_country_code ||
            option.country === member.country_name,
        ) || INDIA_COUNTRY_OPTION;
      setForm({
        business_name: member.business_name || parsedWork.business_name || "",
        role: member.role || parsedWork.role || "",
        experience: member.experience || parsedWork.experience || "",
        business_description: parsedWork.business_description || "",
        city: member.city || "",
        phone: member.phone || "",
        country_code: member.country_code || memberCountry.iso,
        phone_country_code: member.phone_country_code || memberCountry.dial,
        country_name: member.country_name || memberCountry.country,
        residence_type:
          member.residence_type || (memberCountry.iso === "IN" ? "indian" : "nri"),
      });
      setSelectedCountry(memberCountry);
      if (knownProfession) {
        setProfession(memberProfession);
        setCustomProfession("");
      } else {
        setProfession(memberProfession ? "__other__" : "");
        setCustomProfession(memberProfession);
      }
    }
  }, [member]);

  const currentMember = useMemo(() => member || {}, [member]);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const EXPERIENCE_REGEX = /^\d{1,2}$/;
  const handleExperienceChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);
    setForm((f) => ({ ...f, experience: value }));
  };

  const resolvedProfession =
    profession === "__other__" ? customProfession.trim() : profession.trim();

  const fullPhone = [currentMember.phone_country_code, form.phone]
    .filter(Boolean)
    .join(" ");

  const completionItems = useMemo(
    () => [
      { label: "Profile photo", value: currentMember.avatar_path },
      { label: "Full name", value: currentMember.name },
      { label: "Email", value: currentMember.email },
      { label: "Phone number", value: form.phone },
      {
        label: "Country",
        value: form.country_name || currentMember.country_name || currentMember.country_code,
      },
      { label: "City", value: form.city },
      { label: "Profession", value: resolvedProfession },
      { label: "Business / firm name", value: form.business_name },
      { label: "Role / position", value: form.role },
      { label: "Experience", value: form.experience },
      {
        label: "Business description",
        value: form.business_description || currentMember.business_details,
      },
      { label: "Payment status", value: currentMember.payment_status },
      { label: "Membership status", value: currentMember.membership_status },
    ],
    [form, currentMember, resolvedProfession],
  );

  const missingItems = completionItems
    .filter((item) => !String(item.value || "").trim())
    .map((item) => item.label);

  const completeness = Math.round(
    ((completionItems.length - missingItems.length) / completionItems.length) *
      100,
  );

  // =========================
  // SAVE PROFILE
  // =========================
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const businessDetails = composeBusinessDetails(form);
      const countryPayload = {
        country_code: selectedCountry.iso,
        phone_country_code: selectedCountry.dial,
        country_name: selectedCountry.country,
        residence_type: selectedCountry.iso === "IN" ? "indian" : "nri",
      };
      const payload = {
        ...form,
        profession: resolvedProfession,
        ...countryPayload,
      };

      if (businessDetails) {
        payload.business_details = businessDetails;
      }

      if (!payload.phone) {
        delete payload.phone;
      }

      if (!payload.city) {
        delete payload.city;
      }

      await api.patch("/member/me", payload);

      toast.success("Profile updated");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // AVATAR UPLOAD
  // =========================
  const onPickAvatar = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      e.target.value = "";
      return;
    }

    if (f.size > PROFILE_PHOTO_MAX_BYTES) {
      toast.error("Profile photo must be 10 MB or smaller.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);

      await api.post("/member/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Avatar updated");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading || !member) return null;

  return (
    <MemberLayout>
      <div className="max-w-3xl mx-auto space-y-8">
        <h1 className="font-heading text-3xl md:text-4xl font-light text-ink">
          My profile
        </h1>

        <section className="card-premium">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="overline">Member record</div>
              <h2 className="font-heading text-2xl text-ink mt-1">
                {member.name}
              </h2>
              <p className="text-sm text-ink-soft mt-1">
                {member.membership_number || "Membership ID pending"} ·{" "}
                {statusLabel(member.membership_status)}
              </p>
            </div>
            <div className="sm:text-right">
              <div className="inline-flex items-center gap-2 text-sm font-medium text-ink">
                Profile completion: {completeness}%
                <button
                  type="button"
                  onClick={() => setShowMissing((value) => !value)}
                  className="w-7 h-7 rounded-full border border-black/10 bg-white text-ink-muted hover:text-ink inline-flex items-center justify-center"
                  aria-label="Show missing profile details"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-2 w-full sm:w-56 h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-green-600 rounded-full"
                  style={{ width: `${completeness}%` }}
                />
              </div>
              {showMissing ? (
                <div className="mt-3 rounded-2xl border border-black/10 bg-cream-deep p-3 text-left text-xs text-ink-soft shadow-sm">
                  {missingItems.length ? (
                    <>
                      <div className="font-medium text-ink mb-2">
                        Still missing
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {missingItems.map((item) => (
                          <span
                            key={item}
                            className="rounded-full bg-white px-2.5 py-1"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="font-medium text-green-700">
                      Profile is complete.
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ProfileInfo label="Email" value={member.email} />
            <ProfileInfo label="Phone" value={fullPhone || "—"} />
            <ProfileInfo
              label="Country"
              value={member.country_name || member.country_code || "—"}
            />
            <ProfileInfo label="City" value={form.city || "—"} />
            <ProfileInfo
              label="Residence"
              value={
                member.residence_type === "indian"
                  ? "Indian Resident"
                  : "NRI / Overseas"
              }
            />
            <ProfileInfo
              label="Plan amount"
              value={member.amount ? `₹${member.amount}` : "—"}
            />
            <ProfileInfo
              label="Payment status"
              value={statusLabel(member.payment_status)}
            />
            <ProfileInfo
              label="Transaction ID"
              value={member.transaction_id || "—"}
            />
            <ProfileInfo
              label="Submitted on"
              value={formatDate(member.submitted_at || member.created_at)}
            />
            <ProfileInfo
              label="Payment due"
              value={formatDate(member.payment_due_at)}
            />
          </div>
        </section>

        {/* AVATAR */}
        <section className="card-premium flex flex-col sm:flex-row sm:items-center gap-6">
          {getAvatarSrc(member) ? (
            <img
              src={getAvatarSrc(member)}
              alt="avatar"
              className="w-24 h-24 rounded-2xl object-contain bg-white border border-black/10"
            />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-brand/10 text-brand flex items-center justify-center text-2xl font-semibold">
              {member.name
                .split(" ")
                .slice(0, 2)
                .map((p) => p[0])
                .join("")
                .toUpperCase()}
            </div>
          )}

          <div className="flex-1">
            <div className="text-sm font-medium">Profile photo</div>
            <div className="text-xs text-ink-soft">
              Upload any image up to 10 MB. The full image will show in the
              avatar block.
            </div>
          </div>

          <button
            onClick={onPickAvatar}
            disabled={uploading}
            className="btn-secondary w-full sm:w-auto justify-center"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> Upload
              </>
            )}
          </button>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFile}
          />
        </section>

        {/* FORM */}
        <form onSubmit={save} className="card-premium space-y-5">
          {/* BASIC */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-2">
                Qualification / Profession
              </label>
              <select
                className="input-premium"
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
              >
                <option value="">Select qualification / profession</option>
                {PROFESSION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <input
              className="input-premium"
              placeholder="City"
              value={form.city}
              onChange={change("city")}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-2">
              Country / residence
            </label>
            <select
              className="input-premium"
              value={selectedCountry.iso}
              onChange={(e) => {
                const next =
                  COUNTRY_PHONE_OPTIONS.find((opt) => opt.iso === e.target.value) ||
                  INDIA_COUNTRY_OPTION;
                setSelectedCountry(next);
                setForm((f) => ({
                  ...f,
                  country_code: next.iso,
                  phone_country_code: next.dial,
                  country_name: next.country,
                  residence_type: next.iso === "IN" ? "indian" : "nri",
                }));
              }}
            >
              {COUNTRY_PHONE_OPTIONS.map((option) => (
                <option key={option.iso} value={option.iso}>
                  {option.country} · {option.dial} ·{" "}
                  {option.iso === "IN" ? "India" : "NRI"}
                </option>
              ))}
            </select>
          </div>

          {profession === "__other__" ? (
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-2">
                Your qualification / profession
              </label>
              <input
                className="input-premium"
                placeholder="Type your qualification / profession"
                value={customProfession}
                onChange={(e) => setCustomProfession(e.target.value)}
              />
            </div>
          ) : null}

          <input
            className="input-premium"
            placeholder="Phone"
            value={form.phone}
            onChange={change("phone")}
          />

          {/* 🔥 BUSINESS STRUCTURE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="input-premium"
              placeholder="Business / Firm Name"
              value={form.business_name}
              onChange={change("business_name")}
            />
            <input
              className="input-premium"
              placeholder="Role / Position"
              value={form.role}
              onChange={change("role")}
            />
            <input
              className="input-premium"
              placeholder="Experience (in years)"
              value={form.experience}
              onChange={handleExperienceChange}
            />
          </div>

          <textarea
            className="input-premium min-h-[110px]"
            placeholder="Business description / services"
            value={form.business_description}
            onChange={change("business_description")}
            maxLength={BUSINESS_DESCRIPTION_MAX_LENGTH}
          />
          <div className="mt-2 flex items-center justify-between text-[11px] text-ink-muted">
            <span>Brief services / expertise summary</span>
            <span>
              {form.business_description.length}/
              {BUSINESS_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>

          {/* EMAIL */}
          <input
            className="input-premium bg-gray-100 cursor-not-allowed"
            value={member.email}
            disabled
          />

          <button
            type="submit"
            disabled={saving}
            className="btn-primary w-full sm:w-auto justify-center"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="w-4 h-4" /> Save Changes
              </>
            )}
          </button>
        </form>

        <ChangePasswordCard forceOpen={params.get("changePassword") === "1"} />
      </div>
    </MemberLayout>
  );
}

function ProfileInfo({ label, value }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-cream-deep px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-ink break-words">
        {value || "—"}
      </div>
    </div>
  );
}

function ChangePasswordCard({ forceOpen }) {
  const [open, setOpen] = useState(!!forceOpen);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (next.length < 8) {
      toast.error("Minimum 8 characters");
      return;
    }

    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }

    setBusy(true);
    try {
      await api.post("/member/change-password", {
        current_password: cur,
        new_password: next,
      });

      toast.success("Password updated");
      setCur("");
      setNext("");
      setConfirm("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-premium">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium">Change Password</h2>
        <button onClick={() => setOpen(!open)} className="btn-ghost">
          {open ? "Cancel" : "Change"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="relative">
            <input
              type={showCur ? "text" : "password"}
              className="input-premium pr-12"
              placeholder="Current password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowCur(!showCur)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition"
            >
              {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="relative">
            <input
              type={showNext ? "text" : "password"}
              className="input-premium pr-12"
              placeholder="New password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowNext(!showNext)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition"
            >
              {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              className="input-premium pr-12"
              placeholder="Confirm password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition"
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button className="btn-primary w-full" disabled={busy}>
            {busy ? "Updating..." : "Update Password"}
          </button>
        </form>
      )}
    </section>
  );
}
